import React, { useEffect, useState } from 'react';
import { useNavigate, Route, Routes } from 'react-router-dom';
// Okta Imports
import { useOktaAuth } from '@okta/okta-react';
import { Security, SecureRoute, LoginCallback } from '@okta/okta-react';
import { toRelativeUrl } from '@okta/okta-auth-js';

// JSX Components
import Home from './home/Home';
import Navbar from './navbar/Navbar';
import CorsErrorModal from './modals/CorsErrorModal';
import AuthRequiredModal from './modals/AuthRequiredModal';
import TabMenuComponent from './components/TabMenuSplitComponent';
import TestsTab from './components/pages/TestsTab';
import ResultsTab from './components/pages/ResultsTab';
import InfrastructureTab from './components/pages/InfrastructureTab';
import RunTab from './components/pages/RunTab';
import ConfigTab from './components/pages/ConfigTab';
import ArtifactsTab from './components/pages/ArtifactsTab';
import PowerUsersTab from './components/pages/PowerUsersTab';
import ExperimentalResultsTab from './components/pages/ExperimentalTab';
import Protected from './components/Protected';
import DynamicJenkinsParametersPage from './components/templating/DynamicJenkinsParametersPage';
import JenkinsPlans from './components/pages/JenkinsPlans';

var baseURL = process.env.REACT_APP_SSP_BACKEND_URL.endsWith('/') 
  ? process.env.REACT_APP_SSP_BACKEND_URL.slice(0, -1) 
  : process.env.REACT_APP_SSP_BACKEND_URL;

const App = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const navigate = useNavigate();

  const [corsErrorModalOpen, setCorsErrorModalOpen] = React.useState(false);
  const [authRequiredModalOpen, setAuthRequiredModalOpen] = React.useState(false);
  
  // Optional: store user info or token claims in state to display
  const [userInfo, setUserInfo] = useState(null);
  const [claims, setClaims] = useState(null);

  // This effect handles the logic for storing API tokens and forcing login
  useEffect(() => {
    if (authState?.isAuthenticated) {
      setAuthRequiredModalOpen(false);
      const apiToken = localStorage.getItem('apiToken');

     // Get ID Token
      const idToken = authState.idToken;
      // or Access Token
      const accessToken = authState.accessToken;

      // The custom claim, e.g. 'workspace'
      const workspace = idToken?.claims?.workspace;

      // console.log(idToken.claims);

      if (!apiToken) {
        // Fetch the API token and store it
        const fetchApiToken = async () => {
          try {
            const response = await fetch(baseURL + '/api/manageAuth', {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${authState.accessToken.accessToken}`,
              },
            });

            if (response.ok) {
              const data = await response.json();
              localStorage.setItem('apiToken', data.apiToken);
              localStorage.setItem('isAppPowerUser', data.isAppPowerUser);
              localStorage.setItem('apiTokenTimestamp', new Date().getTime().toString());
              navigate('/');
            } else {
              console.error('Failed to fetch API token:', response.statusText);
            }
          } catch (error) {
            console.error('Error fetching API token:', error);
          }
        };
        fetchApiToken();
      }
    } else {
      setAuthRequiredModalOpen(true);
    }

    // Check if apiTokenTimestamp is older than N hours and clear local storage if true
    const clearLocalStorageIfExpired = () => {
      const apiTokenTimestamp = parseInt(localStorage.getItem('apiTokenTimestamp'), 10);
      const eightHoursInMilliseconds = 8 * 60 * 60 * 1000;
      if (apiTokenTimestamp && new Date().getTime() - apiTokenTimestamp > eightHoursInMilliseconds) {
        localStorage.removeItem('apiToken');
        localStorage.removeItem('apiTokenTimestamp');
      }
    };

    clearLocalStorageIfExpired();
    const clearExpiredTokenInterval = setInterval(clearLocalStorageIfExpired, 60 * 60 * 1000); // Check every hour
    return () => clearInterval(clearExpiredTokenInterval);
  }, [authState, navigate, oktaAuth]);

  // This effect demonstrates how to retrieve user info or ID token claims once authenticated
  useEffect(() => {
    if (authState?.isAuthenticated) {
      oktaAuth.getUser()
        .then((info) => {
          console.log('User info with groups:', info);
          // Store groups in localStorage for access control
          if (info.groups) {
            localStorage.setItem('userGroups', JSON.stringify(info.groups));
          }
          setUserInfo(info);
        })
        .catch((err) => console.error('Error getting user info:', err));

        
      // Get ID token claims including groups
      oktaAuth.tokenManager
        .get('idToken')
        .then((idToken) => {
          if (idToken) {
            console.log('ID Token claims with groups:', idToken.claims);
            setClaims(idToken.claims);
          }
        })
        .catch((err) => console.error('Error retrieving ID token:', err));
    }
  }, [authState, oktaAuth]);

  const triggerLogin = async () => {
    navigate('/login');
    await oktaAuth.signInWithRedirect();
  };

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    navigate(toRelativeUrl(originalUri || '/', window.location.origin), { replace: true });
  };

  const customAuthHandler = async () => {
    const previousAuthState = oktaAuth.authStateManager.getPreviousAuthState();
    if (!previousAuthState || !previousAuthState.isAuthenticated) {
      // App initialization stage
      await triggerLogin();
    } else {
      // Ask the user to trigger the login process during token autoRenew process
      setAuthRequiredModalOpen(true);
    }
  };

  return (
    <Security oktaAuth={oktaAuth} onAuthRequired={customAuthHandler} restoreOriginalUri={restoreOriginalUri}>
      <Navbar {...{ setCorsErrorModalOpen }} />
      <CorsErrorModal {...{ corsErrorModalOpen, setCorsErrorModalOpen }} />
      <AuthRequiredModal {...{ authRequiredModalOpen, setAuthRequiredModalOpen, triggerLogin }} />

      <div style={{ marginTop: '100px', padding: '20px', zIndex: '1000' }}>
        <TabMenuComponent />

        <Routes>
          <Route path="/" exact element={
            <>
              <Home />
              {/* {userInfo && (
                <div style={{ margin: '1rem 0' }}>
                  <h4>User info (from /userinfo)</h4>
                  <pre>{JSON.stringify(userInfo, null, 2)}</pre>
                </div>
              )}
              {claims && (
                <div style={{ margin: '1rem 0' }}>
                  <h4>ID Token Claims</h4>
                  <pre>{JSON.stringify(claims, null, 2)}</pre>
                </div>
              )} */}
            </>
          } />
          {/* <Route 
            path="/protected"
            element={
                <SecureRoute>
                <Protected />
                </SecureRoute>
            }
            /> */}
          <Route path="/login/callback" element={<LoginCallback />} />
          <Route path="/tests" element={<TestsTab />} />
          {/* <Route path="/results" element={<ResultsTab />} />
          <Route path="/results-proposal" element={<ExperimentalResultsTab />} /> */}
          <Route path="/infrastructure" element={<InfrastructureTab />} />
          <Route path="/configurations" element={<ConfigTab />} />
          <Route path="/run" element={<RunTab />} />
          <Route path="/artifacts" element={<ArtifactsTab />} />
          <Route path="/power-users" element={<PowerUsersTab />} />
          <Route path="/dynamic-jenkins-parameters-draft" element={<DynamicJenkinsParametersPage />} />
          <Route path="/jenkins-plans" element={<JenkinsPlans />} />
        </Routes>
      </div>
    </Security>
  );
};

export default App;
